import EMAbstractLayerManager from 'src/lib/extreme_map/EMAbstractLayerManager';
import { Style, Circle, Fill, Stroke, Text } from 'ol/style';
import { Feature } from 'ol';
import { Point, MultiLineString } from 'ol/geom';
import type { Options as VectorLayerOptions } from 'ol/layer/BaseVector';
import VectorSource from 'ol/source/Vector';
import { containsCoordinate, type Extent } from 'ol/extent';
import type { Settings as UserSettings } from 'src/models/user';
import type { GeoConnection, KpMap } from 'src/models';
import type { GIKp } from 'src/models/geoItem';
import type { Coordinate } from 'ol/coordinate';
import type { Options as StrokeOptions } from 'ol/style/Stroke';
import { NamedVectorLayer } from 'src/lib/OlMapWrapper';

interface Options {
  isDebugShowGeoConnections?: boolean;
  isDebugShowKpAllLayer?: boolean;
}

interface PointColor {
  fill: string;
  stroke: string;
}

interface LineColor {
  kpLine1: {
    color: string;
    width: number;
  };
  kpLine2: {
    color: string;
    width: number;
  };
  debugConn1: {
    color: string;
    width: number;
  };
}

interface KpColorMap {
  point: {
    strong: PointColor;
    weak: PointColor;
  };
  line: {
    strong: LineColor;
    weak: LineColor;
  };
}

interface KpLayers {
  kpLineLayer: NamedVectorLayer | null;
  kpLayerFiltered: NamedVectorLayer | null;
  kpLayerAll: NamedVectorLayer | null;
}

type KeyOfLineColor = 'kpLine1' | 'kpLine2' | 'debugConn1';

export default class EMKilopostLayerManager extends EMAbstractLayerManager {
  opts: Options;
  isDebugShowGeoConnections? = false;
  isDebugShowKpAllLayer? = false;
  userSettings: UserSettings;
  colorType: 'strong' | 'weak';
  kpMap: KpMap;
  kpLayerAll: NamedVectorLayer | null;
  kpLineLayer: NamedVectorLayer | null;
  kpLayerFiltered: NamedVectorLayer | null;
  pointFeatsFiltered: Feature[];
  private geoConnections: Record<string, Record<string, GeoConnection>> | null = null;

  constructor(opts = {}) {
    super();
    this.opts = opts;
    this.userSettings = {} as UserSettings;
    this.kpMap = {} as KpMap;
    this.kpLayerAll = null;
    this.kpLineLayer = null;
    this.kpLayerFiltered = null;
    this.colorType = 'strong'; // strong|weak
    this.pointFeatsFiltered = [];

    // デバッグ用に接続部を青く表示するかどうか
    this.isDebugShowGeoConnections = this.opts.isDebugShowGeoConnections;
    // KP(全て)のレイヤーを表示するかどうか
    this.isDebugShowKpAllLayer = this.opts.isDebugShowKpAllLayer;
  }

  getKpColorMap_(): KpColorMap {
    return {
      point: {
        strong: {
          fill: '#ff7f00',
          stroke: '#b85b00',
        },
        weak: {
          // 使ってない
          fill: '#ff7f00',
          stroke: '#b85b00',
        },
      },
      line: {
        strong: {
          kpLine1: { color: '#e67200', width: 6 },
          kpLine2: { color: '#ff8c1a', width: 4 },
          debugConn1: { color: '#4286f4', width: 4 },
        },
        weak: {
          kpLine1: { color: '#f2b77d', width: 6 },
          kpLine2: { color: '#ffc48a', width: 4 },
          debugConn1: { color: '#9ec1f9', width: 4 },
        },
      },
    };
  }

  getNewKpPointStyle_({ colorType, text }: { colorType: 'strong' | 'weak'; text: string }): Style {
    const colorMap = this.getKpColorMap_().point[colorType];
    const fillColor = colorMap.fill;
    const strokeColor = colorMap.stroke;
    const ret = new Style({
      image: new Circle({
        radius: 3,
        fill: new Fill({
          color: fillColor,
        }),
        stroke: new Stroke({
          color: strokeColor,
          width: 1,
        }),
      }),
    });
    if (text) {
      ret.setText(new Text({
        text: text,
        font: 'bold 12px sans-serif',
        fill: new Fill({
          color: 'black',
        }),
        stroke: new Stroke({
          color: 'rgba(255, 255, 255, 0.8)',
          width: 2,
        }),
        offsetX: 0,
        offsetY: 12,
      }));
    }
    return ret;
  }

  getNewKpPointFeatureByKpObj_({kp, fixDigits}: { kp: GIKp; fixDigits: number }): Feature {
    const coord = this.coordFromLonLat(kp.location.lon, kp.location.lat);
    const kpText = [
      kp.kpPrefix,
      `(${kp.direction})`,
      kp.placeNameDisp,
      kp.kp.toFixed(fixDigits),
    ].filter(e => !!e).join(' ');
    const info = { id: kp.kpUid, colorType: this.colorType, text: kpText };
    return this.getNewKpPointFeature_(coord, info);
  }

  getNewKpPointFeature_(point: Coordinate, info: { id: string; colorType: 'strong' | 'weak'; text: string }): Feature {
    const feat = new Feature(new Point(point));
    feat.setId(info.id);
    const style = this.getNewKpPointStyle_({
      colorType: info.colorType,
      text: info.text,
    });
    feat.setStyle(style);
    return feat;
  }

  getKpFeaturesByKpId_(kpId: number): { p1: Feature } {
    if (!this.layer) {
      throw new Error('layer is not set');
    }
    const src = this.layer.getSource();
    const p1 = src.getFeatureById(kpId);
    return { p1 };
  }

  getKpMultiLinesFeature_(arrOfLineStringCoords: Coordinate[][], opts: StrokeOptions = {}): Feature {
    const strokeColor = opts.color;
    const strokeWidth = opts.width;

    const multiLineString = new MultiLineString(arrOfLineStringCoords);
    const feat = new Feature(multiLineString);
    const style = new Style({
      stroke: new Stroke({
        color: strokeColor,
        width: strokeWidth,
      }),
    });
    feat.setStyle(style);
    return feat;
  }

  binSearch_(arr: GIKp[], num: number): number {
    // numを見つける. arrは昇順前提.
    let low = 0;
    let high = arr.length - 1;
    let mid, guess;
    while (low <= high) {
      mid = parseInt(((low + high) / 2).toString());
      guess = arr[mid];
      if (Math.abs(guess.kpCalc - num) < Number.EPSILON) {
        return mid;
      } else if (guess.kpCalc > num) {
        high = mid - 1;
      } else {
        low = mid + 1;
      }
    }
    return -1;
  }

  findKpsFromArr(arr: GIKp[], num1: number, num2: number): GIKp[] {
    const ret: GIKp[] = [];
    // num1を見つける. arrは昇順前提.
    let idx = this.binSearch_(arr, num1);
    if (idx === -1) { return ret; }

    if (num1 <= num2) {
      const len = arr.length;
      while (idx < len) {
        const elem = arr[idx];
        if (elem.kpCalc > num2) { break; }
        ret.push(arr[idx++]);
      }
    } else {
      while (idx >= 0) {
        const elem = arr[idx];
        if (elem.kpCalc < num2) { break; }
        ret.push(arr[idx--]);
      }
    }
    return ret;
  }

  findKpFromArr(arr: GIKp[], num: number): GIKp | null {
    const result = this.findKpsFromArr(arr, num, num);
    return result.length > 0 ? result[0] : null;
  }

  getBranches(srcRoadNameDirection: string, geoConnections: Record<string, Record<string, GeoConnection>>): Array<GIKp[]> {
    const ret: Array<GIKp[]> = [];
    const conns = geoConnections[srcRoadNameDirection];
    if (!conns) { return ret; }

    for (const [dstK1, obj1] of Object.entries(conns)) {
      const kParts = dstK1.split('#');
      // 2か3のはず
      if (kParts.length === 2) {
        let tmpArr = this.kpMap.get(srcRoadNameDirection)?.get('main_line');
        const kp1 = this.findKpFromArr(tmpArr ?? [], obj1.srcKpEnd);
        tmpArr = this.kpMap.get(dstK1)?.get('main_line');
        const kp2 = this.findKpFromArr(tmpArr ?? [], obj1.dstKpStart);
        if (kp1 && kp2) {
          ret.push([kp1, kp2]);
        }
      } else {
        const dstK2 = obj1.nextKey;
        const obj2 = obj1.nested[dstK2];
        if (!obj2) continue;
        // 開始
        let tmpArr = this.kpMap.get(srcRoadNameDirection)?.get('main_line');
        const kp1 = this.findKpFromArr(tmpArr ?? [], obj1.srcKpEnd);
        // 経由
        tmpArr = this.kpMap.get(`${kParts[0]}#${kParts[1]}`)?.get(kParts[2]);
        const viaKps = this.findKpsFromArr(
          tmpArr ?? [], obj1.dstKpStart, obj2.srcKpEnd);
        // 終了
        tmpArr = this.kpMap.get(dstK2)?.get('main_line');
        const kp2 = this.findKpFromArr(tmpArr ?? [], obj2.dstKpStart);
        if (kp1 && kp2) {
          ret.push([kp1, ...viaKps, kp2]);
        }
      }
    }
    return ret;
  }

  createLayer_(): void {
    let geoConnections: Record<string, Record<string, GeoConnection>> | null = this.geoConnections;

    const arrOfLineCoords: Coordinate[][] = [];
    const debugArrOfLineCoords: Coordinate[][] = [];
    const pointFeatsFiltered: Feature[] = [];
    const pointFeatsAll: Feature[] = [];
    for (const [k1, obj1] of this.kpMap) {
      // 高速道路を表す太線と、1kmごとの点
      const lines = [];
      const mainLine = obj1.get('main_line');
      if (!mainLine) { continue; }
      lines.push(mainLine);
      if (!geoConnections) {
        throw new Error('geoConnections is null');
      }
      lines.push(...this.getBranches(k1, geoConnections));

      lines.forEach((line, idx) => {
        const coords: Coordinate[] = [];
        const arrLen = line.length;
        line.forEach((kp, idx) => {
          const coord = this.coordFromLonLat(kp.location.lon, kp.location.lat);
          coords.push(coord);

          // 1km (プラス始点と終点) ごと
          const isFirst = idx === 0;
          const isLast = idx === arrLen - 1;
          const tmpVal = parseInt((kp.kpCalc * 1000000).toString());
          const showAnyway = tmpVal === parseInt((tmpVal / 1000000).toString()) * 1000000;
          if (isFirst || isLast || showAnyway) {
            const feat = this.getNewKpPointFeatureByKpObj_({kp, fixDigits: 1});
            pointFeatsFiltered.push(feat);
          }
        });
        arrOfLineCoords.push(coords);
        if (idx > 0) {
          // 本線以外を別配列に保存
          debugArrOfLineCoords.push(coords);
        }
      });

      // KP全て
      if (this.isDebugShowKpAllLayer) {
        for (const lineEnt of obj1) {
          lineEnt[1].forEach(kp => {
            const feat = this.getNewKpPointFeatureByKpObj_({kp, fixDigits: 2});
            pointFeatsAll.push(feat);
          });
        }
      }
    }
    geoConnections = null;

    const colorMap = this.getKpColorMap_().line[this.colorType];
    {
      const multiLineFeat1 = this.getKpMultiLinesFeature_(
        arrOfLineCoords,
        colorMap.kpLine1,
      );
      multiLineFeat1.setId('kpLine1');
      const multiLineFeat2 = this.getKpMultiLinesFeature_(
        arrOfLineCoords,
        colorMap.kpLine2,
      );
      multiLineFeat2.setId('kpLine2');
      const features = [multiLineFeat1, multiLineFeat2];
      if (this.isDebugShowGeoConnections) {
        const multiLineFeat3 = this.getKpMultiLinesFeature_(
          debugArrOfLineCoords,
          colorMap.debugConn1,
        );
        multiLineFeat3.setId('debugConn1');
        features.push(multiLineFeat3);
      }
      const layer = new NamedVectorLayer('kp_line', {
        // title: 'KP line',
        visible: true,
        source: new VectorSource({features: features}),
      });
      this.kpLineLayer = layer;
    }

    {
      const features = pointFeatsFiltered;
      // titleが必要のため、強制型変換
      // https://openlayers.org/en/latest/apidoc/module-ol_layer_Vector-VectorLayer.html
      const layer = new NamedVectorLayer('kiloposts_filtered', {
        title: 'KP',
        visible: false,
        source: new VectorSource({features: features}),
      } as VectorLayerOptions);
      this.kpLayerFiltered = layer;
    }

    if (this.isDebugShowKpAllLayer) {
      const features = pointFeatsAll;
      const layer = new NamedVectorLayer('kiloposts_all', {
        title: 'KP (全て)',
        visible: false,
        source: new VectorSource({features: features}),
      } as VectorLayerOptions);
      this.kpLayerAll = layer;
    }

    this.pointFeatsFiltered = pointFeatsFiltered;
  }

  getLayers(): KpLayers {
    return {
      kpLayerFiltered: this.kpLayerFiltered,
      kpLayerAll: this.kpLayerAll,
      kpLineLayer: this.kpLineLayer,
    };
  }

  prepareLayers(
    userSettings: UserSettings,
    kpMap: KpMap,
    geoConnections: Record<string, Record<string, GeoConnection>>,
  ): KpLayers {
    this.userSettings = userSettings;
    this.kpMap = kpMap;
    this.geoConnections = geoConnections;
    this.createLayer_();
    return this.getLayers();
  }

  refreshLayersOnZoomChange({ zoom, oldZoom }: { zoom?: number; oldZoom?: number }): void {
    if (!this.kpLayerFiltered) return;
    if (oldZoom && zoom && oldZoom >= 17 && zoom < 17) {
      // 17(縮尺100mぐらい)以上のズームをしている状態から17未満にズームアウトしたとき、デフォルトの表示に戻す.
      this.kpLayerFiltered.getSource().clear();
      this.kpLayerFiltered.getSource().addFeatures(this.pointFeatsFiltered);
    }
  }

  refreshLayersOnMoveEnd({ zoom, extent }: { zoom?: number; extent?: Extent }): void {
    // 17よりズームアウトしている状態の場合はすることが無い.
    if ((zoom && zoom < 17) || !this.kpLayerFiltered || !extent) return;
    const pointFeatsAdditional = this.getPointFeatsAdditional_(this.kpMap, extent);
    this.kpLayerFiltered.getSource().addFeatures(pointFeatsAdditional);
  }

  getPointFeatsAdditional_(kpMap: KpMap, extent: Extent): Feature[] {
    const kps = [...kpMap.values()].map(e => [...e.values()]).flat(2);
    const additionalKps = kps.filter(kp => {
      return containsCoordinate(extent, this.convCoord({
        lat: parseFloat(kp.location.lat.toString()),
        lon: parseFloat(kp.location.lon.toString()),
      })) && !this.kpLayerFiltered?.getSource().getFeatureById(kp.kpUid);
    });
    return additionalKps.map(kp => this.getNewKpPointFeatureByKpObj_({kp, fixDigits: 2}));
  }

  refreshKpLineColor(): void {
    if (!this.kpLineLayer) return;
    const colorMap = this.getKpColorMap_().line[this.colorType];
    const featIds: KeyOfLineColor[] = ['kpLine1', 'kpLine2', 'debugConn1'];
    const vectorSource = this.kpLineLayer.getSource();
    for (const featId of featIds) {
      const feat = vectorSource.getFeatureById(featId);
      if (!feat) { continue; }
      const stroke = (feat.getStyle() as Style)?.getStroke();
      stroke.setColor(colorMap[featId].color);
    }
    vectorSource.refresh();
  }

  setDefaultColor(): void {
    this.colorType = 'strong';
    this.refreshKpLineColor();
  }

  setWeakColor(): void {
    this.colorType = 'weak';
    this.refreshKpLineColor();
  }

  getResourceFeatures_(): Feature[] {
    throw new Error('Method not implemented.');
  }
  getResourceStyles_(): Style[] {
    throw new Error('Method not implemented.');
  }
}
